import { useState, useCallback, useEffect, useRef } from 'react';
import type { UseAdminAppReturn } from './useAdminApp';
import type { 
  Composition, 
  ComposingEvent, 
  UpdateCompositionRequest 
} from '@pineseed/spec';

// Configurable debounce delay (milliseconds)
const AUTO_SAVE_DEBOUNCE_MS = 250;

export interface ComposerAdminHandler {
  // State
  composition: Composition | null;
  loading: boolean;
  error: string | null;
  isSaving: boolean;
  
  // Methods
  loadComposition: (participantId: string) => Promise<void>;
  updateComposition: (data: Partial<UpdateCompositionRequest>) => Promise<void>;
  resetComposition: (participantId: string) => Promise<void>;
  
  // Auto-save
  queueAutoSave: (data: Partial<UpdateCompositionRequest>) => void;
  
  // WebSocket events
  composingEvents: Map<string, { timestamp: number; content: string }>;
}

export function useComposerAdmin(adminApp: UseAdminAppReturn): ComposerAdminHandler {
  const [composition, setComposition] = useState<Composition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [composingEvents, setComposingEvents] = useState<Map<string, { timestamp: number; content: string }>>(new Map());
  
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdateRef = useRef<Partial<UpdateCompositionRequest> | null>(null);

  // // Subscribe to composing events via admin WebSocket (once on mount)
  // useEffect(() => {
  //   const handleComposingEvent = (message: unknown) => {
  //     try {
  //       const data = message as { type: string; channel?: string; event?: ComposingEvent };
  //       console.log('📝 [ComposerAdmin] Received message:', data);
        
  //       if (data.type === 'composing' && data.event) {
  //         const { participantId, content } = data.event;
  //         console.log('🟢 [ComposerAdmin] Composing event:', participantId, content.substring(0, 50));
          
  //         // Update composing events map
  //         setComposingEvents((prev) => {
  //           const updated = new Map(prev);
  //           updated.set(participantId, {
  //             timestamp: Date.now(),
  //             content: content.substring(0, 50) + (content.length > 50 ? '...' : ''),
  //           });
  //           return updated;
  //         });
          
  //         // Auto-clear after 500ms
  //         setTimeout(() => {
  //           setComposingEvents((prev) => {
  //             const updated = new Map(prev);
  //             updated.delete(participantId);
  //             return updated;
  //           });
  //         }, 500);
  //       }
  //     } catch (error) {
  //       console.error('🔴 [ComposerAdmin] Failed to process WebSocket message:', error);
  //     }
  //   };

  //   console.log('🟢 [ComposerAdmin] Subscribing to composing channel');
  //   const unsubscribe = adminApp.subscribeToChannel('composing', handleComposingEvent);

  //   return unsubscribe;
  // }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load composition for a participant
  const loadComposition = useCallback(async (participantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/composition?participantId=${participantId}`, {
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load composition: ${response.statusText}`);
      }
      
      const data = await response.json() as Composition;
      console.log('✅ [ComposerAdmin] Loaded composition:', data);
      setComposition(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load composition';
      console.error('🔴 [ComposerAdmin] Load error:', errorMessage);
      setError(errorMessage);
      setComposition(null);
    } finally {
      setLoading(false);
    }
  }, [adminApp]);

  // Update composition (manual save)
  const updateComposition = useCallback(async (data: Partial<UpdateCompositionRequest>) => {
    if (!data.participantId || !data.content || !data.threadId || !data.conversationId || !data.workspaceId) {
      console.warn('⚠️ [ComposerAdmin] Missing required fields for update:', data);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/composition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update composition: ${response.statusText}`);
      }
      
      const updatedComposition = await response.json() as Composition;
      console.log('✅ [ComposerAdmin] Updated composition:', updatedComposition);
      setComposition(updatedComposition);
      
      // Note: Composing events are emitted by the backend API automatically
      // when composition is updated. No need to manually emit via WebSocket here.
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update composition';
      console.error('🔴 [ComposerAdmin] Update error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [adminApp]);

  // Reset composition
  const resetComposition = useCallback(async (participantId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/composition?participantId=${participantId}`, {
        method: 'DELETE',
        headers: { 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to reset composition: ${response.statusText}`);
      }
      
      console.log('✅ [ComposerAdmin] Reset composition for:', participantId);
      setComposition(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset composition';
      console.error('🔴 [ComposerAdmin] Reset error:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [adminApp]);

  // Auto-save with debouncing
  const queueAutoSave = useCallback((data: Partial<UpdateCompositionRequest>) => {
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Store pending update
    pendingUpdateRef.current = data;

    // Set new timeout
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (pendingUpdateRef.current) {
        console.log('💾 [ComposerAdmin] Auto-saving composition...');
        await updateComposition(pendingUpdateRef.current);
        pendingUpdateRef.current = null;
      }
    }, AUTO_SAVE_DEBOUNCE_MS);
  }, [updateComposition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  return {
    composition,
    loading,
    error,
    isSaving,
    loadComposition,
    updateComposition,
    resetComposition,
    queueAutoSave,
    composingEvents,
  };
}

